import { useEffect, useRef, useState } from 'react'
import { Box, Button, Group, Stack, Text, SegmentedControl, Select, Switch } from '@mantine/core'
import { useSetAtom } from 'jotai'
import {
  activeSpeakerIdAtom,
  activeSpeakerNameAtom,
  activeSpeakingAtom,
  currentVisemeAtom,
} from '../atoms/speechAtoms'
import { activeStoryThemeAtom } from '../atoms/themeAtoms'
import { BUILTIN_THEMES } from '../themes/registry'
import { VIZ_STYLES } from '../components/common/speechViz/registry'
import { SpeechVisualizer } from '../components/common/speechViz/SpeechVisualizer'

// A fake character so the visualizers have an id to match the active speaker.
const DEMO_CHARACTER = { id: 'viz-demo-char', name: 'Demo' }

// A short scripted viseme track (value + duration ms) that reads like a spoken
// line — vowels (a/e/o) for the spikes, consonants/silence between. Loops while
// "speaking" so you can watch the styles react without a real story.
const SCRIPT = [
  ['sil', 180], ['k', 70], ['a', 160], ['p', 90], ['t', 80], ['e', 150],
  ['n', 0], ['sil', 120], ['t', 80], ['r', 90], ['a', 200], ['S', 110],
  ['i', 140], ['sil', 90], ['o', 220], ['p', 80], ['@', 130], ['n', 0],
  ['sil', 100], ['f', 80], ['e', 170], ['s', 90], ['t', 80], ['a', 240],
  ['sil', 300],
]

export default function VizDemo() {
  const setViseme = useSetAtom(currentVisemeAtom)
  const setSpeaker = useSetAtom(activeSpeakerIdAtom)
  const setSpeaking_ = useSetAtom(activeSpeakingAtom)
  const setSpeakerName = useSetAtom(activeSpeakerNameAtom)
  const setStoryTheme = useSetAtom(activeStoryThemeAtom)

  const [styleKey, setStyleKey] = useState('bars')
  const [themeId, setThemeId] = useState('novastar')
  const [speaking, setSpeaking] = useState(false)
  const [narratorMode, setNarratorMode] = useState(false)
  const timersRef = useRef([])

  // Apply the chosen theme app-wide so --bw-accent updates and the visualizer's
  // trace color follows (the real story flow does the same via the story theme).
  useEffect(() => {
    setStoryTheme(themeId)
    return () => setStoryTheme(null)
  }, [themeId, setStoryTheme])

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
  }

  const stop = () => {
    clearTimers()
    setSpeaking(false)
    setSpeaker(null)
    setSpeaking_(false)
    setSpeakerName(null)
    setViseme('sil')
  }

  // Walk the scripted track, scheduling each viseme; loop while speaking. In
  // narrator mode we leave the character id null (as the real narrator path
  // does) and rely on activeSpeaking, to verify the bars still animate.
  const runScript = () => {
    setSpeaker(narratorMode ? null : DEMO_CHARACTER.id)
    setSpeaking_(true)
    setSpeakerName(narratorMode ? 'Narrator' : DEMO_CHARACTER.name)
    let acc = 0
    SCRIPT.forEach(([value, dur]) => {
      timersRef.current.push(setTimeout(() => setViseme(value), acc))
      acc += dur
    })
    // Loop.
    timersRef.current.push(
      setTimeout(() => {
        if (timersRef.current.length) runScript()
      }, acc),
    )
  }

  const toggleSpeak = () => {
    if (speaking) {
      stop()
    } else {
      setSpeaking(true)
      clearTimers()
      runScript()
    }
  }

  useEffect(() => () => stop(), []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--bw-bg)',
        color: 'var(--bw-body-color)',
        padding: 32,
      }}
    >
      <Stack gap="lg" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div>
          <Text size="xl" fw={700} ff="var(--bw-heading-font)">
            Speech visualizer styles
          </Text>
          <Text size="sm" c="dimmed">
            All four are driven by the viseme stream (CORS-safe). Hit “Simulate
            speech” to see them react, switch themes to check the trace color.
          </Text>
        </div>

        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group gap="md" align="center">
            <Button
              onClick={toggleSpeak}
              color={speaking ? 'red' : 'cyan'}
              variant={speaking ? 'filled' : 'light'}
            >
              {speaking ? '■ Stop' : '▶ Simulate speech'}
            </Button>
            <Switch
              label="Narrator (no character id)"
              checked={narratorMode}
              onChange={(e) => setNarratorMode(e.currentTarget.checked)}
              disabled={speaking}
            />
          </Group>
          <Group gap="lg">
            <div>
              <Text size="xs" c="dimmed" mb={4}>Theme</Text>
              <Select
                value={themeId}
                onChange={(v) => v && setThemeId(v)}
                data={BUILTIN_THEMES.map((t) => ({ value: t.id, label: t.name }))}
                allowDeselect={false}
                w={180}
              />
            </div>
          </Group>
        </Group>

        {/* Focused preview of the currently selected style at hero size. */}
        <Box>
          <Group justify="space-between" mb={6}>
            <Text size="sm" fw={600}>Selected style</Text>
            <SegmentedControl
              value={styleKey}
              onChange={setStyleKey}
              data={VIZ_STYLES.map((s) => ({ value: s.key, label: s.label }))}
            />
          </Group>
          <SpeechVisualizer character={DEMO_CHARACTER} styleKey={styleKey} height={120} />
        </Box>

        {/* All styles side by side so you can compare at a glance. */}
        <div>
          <Text size="sm" fw={600} mb={6}>All styles</Text>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 16,
            }}
          >
            {VIZ_STYLES.map((s) => (
              <Box key={s.key}>
                <Text size="xs" c="dimmed" mb={4}>{s.label}</Text>
                <SpeechVisualizer character={DEMO_CHARACTER} styleKey={s.key} height={90} />
              </Box>
            ))}
          </Box>
        </div>
      </Stack>
    </Box>
  )
}
